import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Award, Eye } from "lucide-react";
import { motion } from "framer-motion";
import { BADGE_DEFINITIONS, UserBadge, getBadgeDefinition, getBadgeRarityColor } from "@/types/badges";
import { useState } from "react";
import { AllBadgesModal } from "./AllBadgesModal";

interface BadgeGridProps {
  badges: UserBadge[];
}

export const BadgeGrid = ({ badges }: BadgeGridProps) => {
  const [showAllBadges, setShowAllBadges] = useState(false);
  const earnedBadgesCount = badges.length;
  
  // Get earned badge definitions only
  const earnedBadgeDefinitions = badges.map(userBadge => 
    getBadgeDefinition(userBadge.badge_slug)
  ).filter((badgeDefn): badgeDefn is NonNullable<typeof badgeDefn> => badgeDefn !== null);

  return (
    <>
      <Card className="bg-black/20 backdrop-blur-sm border-purple-500/50 shadow-2xl">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Award className="w-5 h-5" />
            Badge Collection
            <Badge className="ml-auto bg-purple-500 text-white">
              {earnedBadgesCount} earned
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {earnedBadgeDefinitions.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {earnedBadgeDefinitions.map((badgeDefinition) => {
                const earnedBadge = badges.find(b => b.badge_slug === badgeDefinition.slug);
                
                return (
                  <motion.div
                    key={badgeDefinition.slug}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                    className={`p-4 rounded-lg text-center transition-all duration-300 cursor-pointer border-2 bg-gradient-to-br ${getBadgeRarityColor(badgeDefinition.rarity)} border-yellow-300 shadow-lg`}
                    title={earnedBadge ? `Earned on ${new Date(earnedBadge.earned_at).toLocaleDateString()}` : ''}
                  >
                    <div className="text-3xl mb-2">{badgeDefinition.icon}</div>
                    <h3 className="font-bold text-sm text-white">
                      {badgeDefinition.name}
                    </h3>
                     <p className="text-xs text-white/80">
                      {badgeDefinition.description}
                    </p>
                    <div className="mt-2 space-y-1">
                      <Badge className="bg-white/20 text-white">
                        ✓ Unlocked
                      </Badge>
                      <div className="text-xs text-white/60">
                        {new Date(earnedBadge!.earned_at).toLocaleDateString()}
                      </div>
                      {badgeDefinition.requirement && (
                        <div className="text-xs text-white/50">
                          {badgeDefinition.requirement}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400">No badges earned yet</p>
            </div>
          )}
          
          <div className="text-center">
            <Button
              onClick={() => setShowAllBadges(true)}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <Eye className="w-4 h-4 mr-2" />
              View All {BADGE_DEFINITIONS.length} Badges
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <AllBadgesModal 
        isOpen={showAllBadges}
        onClose={() => setShowAllBadges(false)}
        userBadges={badges}
      />
    </>
  );
};